import type { Root } from "./prototypeTypes";
import {
  WATERMARK_OVERLAY_ID,
  type WatermarkIdentity,
} from "./videoWatermarkTypes";
import { buildIdentity, marqueeText } from "./videoWatermarkIdentity";
import {
  ensureContainerRelative,
  hardenVideoElement,
  injectWatermarkStyles,
  preventDefault,
} from "./videoWatermarkStyle";

/**
 * Anti-piracy video watermark: two horizontal marquee bands on top of every
 * video surface, plus hardened <video> elements. Combined with worker-side
 * session/device-bound opaque grants (privateMediaRoute.ts), sharing a screen or
 * scraping DevTools yields a privacy-safe opaque trace that maps to a
 * short-lived server-side content grant without exposing account identity.
 */
interface WatermarkState {
  identity: WatermarkIdentity;
  mountedContainers: Set<HTMLElement>;
  observer?: MutationObserver;
}

const state: WatermarkState = {
  identity: buildIdentity({}),
  mountedContainers: new Set(),
};

function buildFloatingStamp(): HTMLDivElement {
  const stamp = document.createElement("div");
  stamp.className = `${WATERMARK_OVERLAY_ID}-floating`;
  stamp.textContent = marqueeText(state.identity);
  return stamp;
}

function mountInto(container: HTMLElement) {
  if (!state.identity.traceCode) return;
  if (state.mountedContainers.has(container)) {
    if (container.querySelectorAll(`.${WATERMARK_OVERLAY_ID}-floating`).length >= 2) return;
    disposeContainer(container);
  }
  injectWatermarkStyles();
  ensureContainerRelative(container);

  const stamps: HTMLDivElement[] = [];
  const paths = Array.from({ length: 2 }, (_, index) => {
    const base = index === 0 ? 18 : 58;
    const jitter = () => `${Math.max(8, Math.min(82, base + Math.round((Math.random() - 0.5) * 22)))}vh`;
    return [jitter(), jitter(), jitter(), "0deg", `${34 + Math.round(Math.random() * 12)}s`, `${-Math.round(Math.random() * 24)}s`];
  });
  for (let i = 0; i < paths.length; i++) {
    const stamp = buildFloatingStamp();
    const [start, mid, end, angle, duration, delay] = paths[i];
    stamp.style.setProperty("--dm-watermark-y-start", start);
    stamp.style.setProperty("--dm-watermark-y-mid", mid);
    stamp.style.setProperty("--dm-watermark-y-end", end);
    stamp.style.setProperty("--dm-watermark-angle", angle);
    stamp.style.setProperty("--dm-watermark-duration", duration);
    stamp.style.setProperty("--dm-watermark-delay", delay);
    container.appendChild(stamp);
    stamps.push(stamp);
  }
  const refreshInterval = window.setInterval(() => {
    const text = marqueeText(state.identity);
    stamps.forEach((stamp, idx) => {
      stamp.textContent = text;
    });
  }, 60_000);
  (container as any)._dmJitterInterval = refreshInterval;

  container.querySelectorAll("video").forEach((node) => {
    if (node instanceof HTMLVideoElement) hardenVideoElement(node);
  });

  state.mountedContainers.add(container);
}

function scanAndMount(root: Root) {
  const containers: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();
  for (const selector of ["video"]) {
    root.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.closest(".video-fullscreen:not(.active)")) return;
      const target =
        node.tagName === "VIDEO"
          ? node.parentElement instanceof HTMLElement
            ? node.parentElement
            : node
          : node;
      if (!seen.has(target)) {
        seen.add(target);
        containers.push(target);
      }
    });
  }
  for (const container of containers) mountInto(container);
  // Harden audio elements — single-layer scan replaces the per-container observer
  root.querySelectorAll("audio").forEach((node) => {
    if (node instanceof HTMLAudioElement) {
      node.setAttribute("controlsList", "nodownload");
      node.setAttribute("oncontextmenu", "return false");
      node.addEventListener("contextmenu", preventDefault, { passive: false });
    }
  });
}

export function startVideoWatermark(
  root: Root,
) {
  injectWatermarkStyles();
  scanAndMount(root);

  if (!state.observer) {
    // P1-12: 防抖 + 收窄扫描范围, 避免 subtree:true 在任意 DOM 变动时全文档扫描风暴。
    // rAF 合并同一帧内的多次 mutation 批次, 只触发一次 scanAndMount。
    let scanScheduled = false;
    state.observer = new MutationObserver(() => {
      if (scanScheduled) return;
      scanScheduled = true;
      requestAnimationFrame(() => {
        scanScheduled = false;
        scanAndMount(root);
      });
    });
    state.observer.observe(document.body, { childList: true, subtree: true });
  }
}

export function updateVideoWatermarkTrace(root: Root, traceCode: string): void {
  state.identity = buildIdentity({ traceCode });
  for (const container of state.mountedContainers) {
    container.querySelectorAll(`.${WATERMARK_OVERLAY_ID}-floating`).forEach((node) => {
      node.textContent = marqueeText(state.identity);
    });
  }
  scanAndMount(root);
}

export function stopVideoWatermark() {
  if (state.observer) {
    state.observer.disconnect();
    state.observer = undefined;
  }
  [...state.mountedContainers].forEach(disposeContainer);
  state.mountedContainers = new Set();
}

export function stopVideoWatermarksIn(scope: ParentNode | string): void {
  const root =
    typeof scope === "string"
      ? document.querySelector(scope)
      : scope;
  if (!(root instanceof HTMLElement) && !(root instanceof Document)) return;
  for (const container of [...state.mountedContainers]) {
    if (root === container || root.contains(container)) disposeContainer(container);
  }
}

function disposeContainer(container: HTMLElement): void {
  container
    .querySelectorAll(`.${WATERMARK_OVERLAY_ID}-floating`)
    .forEach((node) => node.remove());
  if ((container as any)._dmJitterInterval) clearInterval((container as any)._dmJitterInterval);
  delete (container as any)._dmJitterInterval;
  container.classList.remove(`${WATERMARK_OVERLAY_ID}-protected`);
  container.removeEventListener("contextmenu", preventDefault);
  container.removeEventListener("dragstart", preventDefault);
  state.mountedContainers.delete(container);
}

export const __deepmoxWatermarkTestHooks = {
  marqueeText,
  WATERMARK_OVERLAY_ID,
  state,
};
